import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { GitMerge, Loader2, X } from 'lucide-react'
import { collectionsApi } from '../../services/collectionsApi'
import { renderCollectionItem } from '../../utils/collectionUtils'
import type { Collection, CollectionItem } from '../../types/collections'

/**
 * Review/edit/approve modal for the entity-merge (de-duplication) flow — the
 * final step of Stage 6. On open it fires the LLM `merge/propose` call and, in
 * parallel, loads the selected candidate items (so the survivor selector can
 * show a human label per candidate regardless of whether the calling surface
 * had their `data` cached — Places, for one, doesn't).
 *
 * The proposal (`proposed_data`, keyed by `config.fields` names) is rendered as
 * an editable form: every proposed field is an input the user can tweak before
 * committing. A radio selector lets the user override which candidate becomes
 * the survivor (defaulting to the LLM's recommendation). "Approve & merge"
 * posts the (possibly edited) data + chosen survivor to `merge/commit`; on
 * success the caller closes the modal, clears the selection, refreshes the list
 * and toasts the re-point summary.
 *
 * Rate-limit (429) and validation errors surface inline; 429 gets a Retry that
 * re-fires propose without losing the modal. No fake/mocked proposal — if the
 * real LLM call fails the modal says so.
 *
 * Animation reuses the CLAUDE.md surface preset verbatim (stiffness 500 /
 * damping 30, opacity + scale 0.95→1 + y); the close button gets the rotate:90
 * hover reserved for close/cancel affordances.
 */

const surfaceSpring = { type: 'spring', stiffness: 500, damping: 30 } as const
const buttonSpring = { type: 'spring', stiffness: 400, damping: 20 } as const

interface MergeSummary {
    survivor: CollectionItem
    repointed_checkin_count: number
    reaped_loser_ids: number[]
}

interface MergeReviewModalProps {
    /** The collection being de-duplicated (needs `config.fields`). */
    collection: Collection
    /** The full selected candidate set (survivor + losers), 2..10. */
    itemIds: number[]
    onClose: () => void
    /** Fired after a successful commit with the re-point summary. */
    onMerged: (summary: MergeSummary) => void
}

// Coerce a form value back to the field's declared type before committing, so
// number inputs (which yield strings) round-trip as numbers. Empty → null for
// optional clearing; the backend fills schema_version.
function coerceValue(value: any, type?: string): any {
    if (value === '' || value === undefined) return null
    switch (type) {
        case 'int':
            return value === null ? null : parseInt(value, 10)
        case 'decimal':
            return value === null ? null : parseFloat(value)
        case 'boolean':
            return Boolean(value)
        default:
            return value
    }
}

const MergeReviewModal: React.FC<MergeReviewModalProps> = ({
    collection,
    itemIds,
    onClose,
    onMerged,
}) => {
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [rateLimited, setRateLimited] = useState(false)

    const [candidates, setCandidates] = useState<CollectionItem[]>([])
    const [recommendedId, setRecommendedId] = useState<number | null>(null)
    const [chosenSurvivorId, setChosenSurvivorId] = useState<number | null>(null)
    const [formData, setFormData] = useState<Record<string, any>>({})

    const [submitting, setSubmitting] = useState(false)
    const [commitError, setCommitError] = useState<string | null>(null)

    // Field ordering / type lookup from the collection schema. We render the
    // proposed fields in schema position order; fields the LLM didn't return
    // simply don't appear.
    const fieldOrder = useMemo(() => {
        return Object.entries(collection.config?.fields ?? {})
            .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
            .map(([name]) => name)
    }, [collection])

    const load = async () => {
        setLoading(true)
        setLoadError(null)
        setRateLimited(false)
        try {
            const [proposal, items] = await Promise.all([
                collectionsApi.items.mergePropose(collection.id, itemIds),
                Promise.all(
                    itemIds.map((id) => collectionsApi.items.get(collection.id, id))
                ),
            ])
            setCandidates(items)
            setRecommendedId(proposal.recommended_survivor_id)
            // Default the survivor to the LLM recommendation, but only if it's
            // actually one of the selected candidates (defensive).
            const rec = itemIds.includes(proposal.recommended_survivor_id)
                ? proposal.recommended_survivor_id
                : itemIds[0]
            setChosenSurvivorId(rec)
            setFormData({ ...proposal.proposed_data })
        } catch (err: any) {
            const status = err?.response?.status
            if (status === 429) {
                setRateLimited(true)
                setLoadError(
                    'The merge assistant is rate-limited right now. Wait a moment and retry.'
                )
            } else {
                const detail = err?.response?.data?.detail
                setLoadError(
                    typeof detail === 'string'
                        ? detail
                        : 'Failed to build a merge proposal. Please try again.'
                )
            }
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleFieldChange = (fieldName: string, value: any) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value }))
    }

    const handleSubmit = async () => {
        if (chosenSurvivorId == null) return
        setSubmitting(true)
        setCommitError(null)

        // Coerce each proposed field back to its schema type.
        const data: Record<string, any> = {}
        for (const [key, value] of Object.entries(formData)) {
            data[key] = coerceValue(value, collection.config?.fields?.[key]?.type)
        }

        try {
            const summary = await collectionsApi.items.mergeCommit(
                collection.id,
                chosenSurvivorId,
                data,
                itemIds
            )
            onMerged(summary)
        } catch (err: any) {
            const status = err?.response?.status
            const detail = err?.response?.data?.detail
            if (status === 429) {
                setCommitError(
                    'Rate-limited while committing. Wait a moment and try again.'
                )
            } else {
                setCommitError(
                    typeof detail === 'string'
                        ? detail
                        : 'Failed to commit the merge. Please try again.'
                )
            }
        } finally {
            setSubmitting(false)
        }
    }

    // Ordered list of proposed field names (schema order first, then any extra
    // keys the proposal returned that aren't in the ordered set).
    const proposedFieldNames = useMemo(() => {
        const present = new Set(Object.keys(formData))
        const ordered = fieldOrder.filter((n) => present.has(n))
        const extras = Object.keys(formData).filter((n) => !fieldOrder.includes(n))
        return [...ordered, ...extras]
    }, [formData, fieldOrder])

    const renderFieldInput = (fieldName: string) => {
        const field = collection.config?.fields?.[fieldName]
        const type = field?.type
        const value = formData[fieldName]
        const strValue = value == null ? '' : value

        switch (type) {
            case 'int':
                return (
                    <input
                        type="number"
                        value={strValue}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                )
            case 'decimal':
                return (
                    <input
                        type="number"
                        step="0.01"
                        value={strValue}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                )
            case 'boolean':
                return (
                    <select
                        value={value === true ? 'true' : value === false ? 'false' : ''}
                        onChange={(e) =>
                            handleFieldChange(fieldName, e.target.value === '' ? null : e.target.value === 'true')
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    >
                        <option value="">Select...</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                )
            case 'picture':
                return (
                    <div className="space-y-2">
                        {strValue && (
                            <img
                                src={strValue}
                                alt={fieldName}
                                className="h-20 w-20 rounded-lg object-cover border border-gray-200"
                            />
                        )}
                        <input
                            type="url"
                            value={strValue}
                            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                            placeholder="https://..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                        />
                    </div>
                )
            case 'url':
                return (
                    <input
                        type="url"
                        value={strValue}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                )
            case 'email':
                return (
                    <input
                        type="email"
                        value={strValue}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        placeholder="user@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                )
            default:
                return (
                    <input
                        type="text"
                        value={strValue}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
                    />
                )
        }
    }

    const candidateLabel = (item: CollectionItem): string => {
        const rendered = renderCollectionItem(item, collection)
        return rendered && rendered.trim() ? rendered : `Item #${item.id}`
    }

    return (
        <AnimatePresence>
            <motion.div
                // z-index inline so the overlay stacks correctly even if the
                // arbitrary `z-[1200]` utility isn't in the compiled CSS. Centering
                // is via flexbox (robust); only the stacking value needs pinning.
                style={{ zIndex: 1200 }}
                className="fixed inset-0 flex items-center justify-center p-4 bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                    transition={surfaceSpring}
                    style={{ transformOrigin: 'bottom center' }}
                    className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
                        <div className="flex items-center gap-2">
                            <GitMerge className="w-5 h-5 text-amber-800" />
                            <h2 className="text-xl font-semibold">Review merge</h2>
                            <span className="text-sm text-gray-500">
                                from {itemIds.length} candidate{itemIds.length === 1 ? '' : 's'}
                            </span>
                        </div>
                        <motion.button
                            type="button"
                            onClick={onClose}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            transition={buttonSpring}
                            className="p-1 text-gray-500 hover:text-gray-900 rounded-lg"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </motion.button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                <Loader2 className="w-8 h-8 animate-spin mb-3 text-amber-800" />
                                <p className="text-sm">Asking the merge assistant to synthesize one entity…</p>
                            </div>
                        )}

                        {!loading && loadError && (
                            <div className="py-8 text-center space-y-4">
                                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm">
                                    {loadError}
                                </div>
                                {rateLimited && (
                                    <motion.button
                                        type="button"
                                        onClick={load}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={buttonSpring}
                                        className="px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark"
                                    >
                                        Retry
                                    </motion.button>
                                )}
                            </div>
                        )}

                        {!loading && !loadError && (
                            <>
                                {/* Survivor selector */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                        Surviving record
                                    </h3>
                                    <p className="text-xs text-gray-500 mb-2">
                                        The chosen record keeps its id and inherits every check-in
                                        the others were tagged in. The others are removed.
                                    </p>
                                    <div className="space-y-2">
                                        {candidates.map((cand) => (
                                            <label
                                                key={cand.id}
                                                className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                                    chosenSurvivorId === cand.id
                                                        ? 'border-amber-500 bg-amber-50'
                                                        : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="survivor"
                                                    checked={chosenSurvivorId === cand.id}
                                                    onChange={() => setChosenSurvivorId(cand.id)}
                                                    className="text-amber-800 focus:ring-action"
                                                />
                                                <span className="text-sm text-gray-800 flex-1 truncate">
                                                    {candidateLabel(cand)}
                                                </span>
                                                {recommendedId === cand.id && (
                                                    <span className="text-[11px] font-medium text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">
                                                        Recommended
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-gray-400 shrink-0">
                                                    #{cand.id}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Editable proposed fields */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                                        Merged details
                                    </h3>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Proposed by the merge assistant — edit anything before approving.
                                    </p>
                                    {proposedFieldNames.length === 0 ? (
                                        <p className="text-sm text-gray-400 italic">
                                            The proposal returned no fields.
                                        </p>
                                    ) : (
                                        <div className="space-y-4">
                                            {proposedFieldNames.map((fieldName) => (
                                                <div key={fieldName}>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {fieldName}
                                                    </label>
                                                    {renderFieldInput(fieldName)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {commitError && (
                                    <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm">
                                        {commitError}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0 bg-white">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <motion.button
                            type="button"
                            onClick={handleSubmit}
                            disabled={loading || !!loadError || submitting || chosenSurvivorId == null}
                            whileHover={
                                loading || !!loadError || submitting ? undefined : { scale: 1.05 }
                            }
                            whileTap={
                                loading || !!loadError || submitting ? undefined : { scale: 0.95 }
                            }
                            transition={buttonSpring}
                            className="flex items-center gap-1.5 px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Merging…
                                </>
                            ) : (
                                <>
                                    <GitMerge className="w-4 h-4" />
                                    Approve &amp; merge
                                </>
                            )}
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

export default MergeReviewModal
